// AXIOM — PortalOverlay
// Everything a viewer's action puts on the player's screen: the sign-curse
// countdown, the prime rain, the right-rail toasts — and the receipts for the
// free tier: the like gauge, the chat bubbles, the enemy nameplates and the
// free-action price list. A like with no gauge is pressed once and never again.
//
// It reads almost nothing from the game. Bus events drive the whole layer, so
// it renders correctly when it is the only thing that has booted — a curse that
// lands during the loading gate still gets a pill and a live clock. The one
// exception is the nameplates, which read S.camera to follow their enemy; a
// missing camera hides them, it never throws.
//
//   import { initPortalOverlay } from './ui/PortalOverlay.js';
//   initPortalOverlay();   // idempotent

import './styles.css';
import { Vector3 } from 'three';
import { h } from './FormulaBar.js';
import { rankingView } from './PortalRankingView.js';
import { bus } from '../core/EventBus.js';
import S from '../core/Registry.js';

const PRIMES = ['2', '3', '5', '7', '11', '13', '17', '19', '23'];
const RAIN_PER_DENSITY = 14;
// Ten bursts arriving in the same frame must degrade, not stall the compositor:
// past this many live glyphs, new ones are simply not minted.
const RAIN_CAP = 120;

const TOAST_MAX = 4;
const TOAST_HOLD = 2600;
const TOAST_FADE = 320;
const TOAST_MAX_CHARS = 140;

// Chat bubbles live in the toast rail but hold a beat longer: a toast reports
// game feedback, a bubble proves a person's comment reached the world.
const CHAT_MAX = 3;
const CHAT_HOLD = 3500;
const CHAT_FADE = 300;
// Belt over the SDK's braces: strings arrive guardText'd (one line, bounded),
// but this layer clamps again so a contract slip elsewhere cannot grow the DOM.
const CHAT_NAME_CHARS = 24;
const CHAT_TEXT_CHARS = 120;

const LIKE_FALLBACK_GOAL = 50;   // the manifest's own いいね→雨 threshold
const LIKE_BURST = 700;

const TAG_MAX = 8;
const TAG_FADE = 260;
const TAG_NAME_CHARS = 24;
// A plate a hair past the viewport edge still tracks; one far outside is not
// styled at all. The margin keeps plates from popping at the very edge.
const TAG_EDGE = 48;
const TAG_RISE = 0.5;            // metres of air between scalp and name

const HEX_CLEAR = 600;
// A deadline that passes without an `active:false` cannot leave the pill on
// screen forever. The game is authoritative about the release, so an orphaned
// pill leaves quietly — no green flash, because nobody cured anything.
const HEX_ORPHAN = 1200;
// A curse with no readable deadline still has to be visible. 5s is the
// manifest's own preview duration, mid-band of the 3–8s the action allows.
const HEX_FALLBACK = 5000;

// ⊖ is already the formula bar's glyph for a shot whose sign is against you.
const TONE_MARK = { help: '⊕', harm: '⊖', info: '◇' };

// One scratch vector for every projection this layer ever does.
const _tv = new Vector3();

class PortalOverlay {
  constructor(host) {
    this.root = h('div', 'ax-portal');
    this.rainEl = h('div', 'ax-portal-rain');
    this.toastEl = h('div', 'ax-portal-toasts');
    this.root.append(this.rainEl, this.toastEl);
    host.appendChild(this.root);

    this.hexEl = null;
    this.hexNameEl = null;
    this.hexNumEl = null;
    this.hexFillEl = null;
    this.hexUntil = 0;
    this.hexSpan = HEX_FALLBACK;
    this._raf = 0;
    this._clearT = 0;
    this._orphanT = 0;
    this._fb = null;

    this.toasts = [];
    this.chats = [];

    // like gauge — built on the first batch, never before
    this.likeEl = null;
    this.likeNumEl = null;
    this.likeFillEl = null;
    this._likeK = 0;
    this._likeBurstT = 0;

    // free-action price list — built on the first proof of a live session
    this.menuEl = null;
    this._menuGoalEl = null;
    this._live = false;

    // nameplates — enemy object → { enemy, el, dying, killT }, insertion-ordered
    // so "evict the oldest" is just the Map's first key
    this.tags = new Map();
    this._tagRaf = 0;
    this._tagStep = () => {
      this._tagRaf = 0;
      if (!this.tags.size) return;
      const cam = S.camera;
      const w = window.innerWidth, ht = window.innerHeight;
      for (const rec of this.tags.values()) {
        const e = rec.enemy;
        // Death without an untag (a dropped event, a despawn path the wiring
        // missed) must not leave a name floating over a corpse.
        if (e.alive === false && !rec.dying) this._untag(e);
        let vis = false;
        if (cam && e.position) {
          const top = e.position.y + (Number(e.type && e.type.height) || 2) + TAG_RISE;
          _tv.set(e.position.x, top, e.position.z).project(cam);
          if (_tv.z > -1 && _tv.z < 1) {
            const sx = (1 + _tv.x) * 0.5 * w;
            const sy = (1 - _tv.y) * 0.5 * ht;
            vis = sx > -TAG_EDGE && sx < w + TAG_EDGE && sy > -TAG_EDGE && sy < ht + TAG_EDGE;
            // whole-pixel snapping: a nameplate that shimmers on sub-pixels
            // reads as jitter on the enemy itself
            if (vis) rec.el.style.transform = `translate(${Math.round(sx)}px, ${Math.round(sy)}px) translate(-50%, -100%)`;
          }
        }
        rec.el.classList.toggle('is-off', !vis);
      }
      this._tagRaf = requestAnimationFrame(this._tagStep);
    };

    this._tick = () => {
      this._raf = 0;
      const left = Math.max(0, (this.hexUntil - performance.now()) / 1000);
      this.hexNumEl.textContent = left.toFixed(1);
      const k = Math.min(1, (left * 1000) / this.hexSpan);
      this.hexFillEl.style.transform = `scaleX(${k.toFixed(4)})`;
      if (left > 0) { this._raf = requestAnimationFrame(this._tick); return; }
      this._orphanT = setTimeout(() => this._hexTeardown(), HEX_ORPHAN);
    };

    this._onResize = () => this._placeHex();
    window.addEventListener('resize', this._onResize, { passive: true });

    this.boardEl = null;
    this._board = null;
    this._score = null;

    this._offs = [
      bus.on('portal:hex', (p) => (p && p.active ? this._hexArm(p) : this._hexRelease())),
      bus.on('portal:rain', (p) => this._rain(p)),
      bus.on('portal:toast', (p) => this._toast(p)),
      bus.on('portal:board', (p) => { if(p?.source!=='TRAIDENT_PUBLIC'&&p?.track==='LIVE_INTERACTIVE')this._liveSeen(); this._board = p || null; this._renderBoard(); }),
      bus.on('portal:score', (p) => { this._score = p?.state === 'idle' ? null : (p || null); this._renderBoard(); }),
      bus.on('portal:like', (p) => { this._liveSeen(); this._like(p); }),
      bus.on('portal:chat', (p) => { this._liveSeen(); this._chat(p); }),
      bus.on('portal:tag', (p) => this._tag(p)),
      bus.on('portal:untag', (p) => this._untag(p && p.enemy)),
      // The board belongs to the pauses in play: death screen and menu. During
      // a run it would sit on top of the fight for no one's benefit.
      bus.on('state:phase', ({ phase }) => this._boardVisible(phase === 'dead' || phase === 'menu', phase)),
      // A new run inherits nothing — the curse, the rain and the toasts all
      // belonged to the run that just ended.
      bus.on('ui:restart', () => this.clear()),
    ];
  }

  /* ── leaderboard panel ─────────────────────────────────────────────────── */

  _renderBoard() {
    const view = rankingView(this._board, this._score);
    if (!this.boardEl) {
      this.boardEl = h('div', 'ax-portal-board');
      // NOT inside this.root. That layer is z-index 22 — deliberately under
      // .ax-screens (40), which is right for the rain and the toasts. But the
      // board is only ever shown WHILE a full-bleed screen is open, and a child
      // cannot outrank its own stacking context, so living in the layer meant
      // rendering perfectly and never being seen. It gets the body and its own
      // z-index instead.
      document.body.appendChild(this.boardEl);
    }
    const rows = view.entries.map((entry) => h('div',
      'ax-portal-board-row' + (entry.isMe ? ' is-me' : ''),
      h('span', 'ax-portal-board-rank', `#${entry.rank}`),
      h('span', 'ax-portal-board-name', entry.name || 'ANONYMOUS'),
      h('span', 'ax-portal-board-score', `${entry.score}桁`)));
    this.boardEl.replaceChildren(
      h('div', 'ax-portal-board-head',
        h('span', 'ax-portal-board-kicker', 'TRAIDENT / RANKING'),
        h('div', 'ax-portal-board-title', '最大ダメージ順位')),
      h('div', `ax-portal-board-status is-${view.status.kind}`,
        h('span', 'ax-portal-board-status-label', view.status.label),
        h('span', 'ax-portal-board-status-copy', view.status.text)),
      h('div', 'ax-portal-board-podium',
        h('div', 'ax-portal-board-section', '現在の上位'),
        ...(rows.length ? rows : [h('div', 'ax-portal-board-empty', 'まだ公開記録がありません')]),
      ),
      h('div', `ax-portal-board-mine is-${view.player.kind}`,
        h('span', 'ax-portal-board-mine-label', view.player.label),
        h('strong', 'ax-portal-board-mine-rank', view.player.value),
        view.player.score ? h('span', 'ax-portal-board-mine-score', view.player.score) : null,
        h('span', 'ax-portal-board-mine-note', view.player.note)),
      h('div', 'ax-portal-board-ticket',
        h('span', 'ax-portal-board-direction', view.direction),
        h('span', 'ax-portal-board-ticket-copy', view.ticket)),
    );
    const phase = S.state?.phase;
    if(view.publicRead){
      for(const [track,label] of [['STANDARD','通常'],['LIVE_INTERACTIVE','ライブ']]){
        const tab=h('button','ax-ranking-action',label);tab.type='button';tab.setAttribute('aria-pressed',String(view.track===track));tab.onclick=()=>bus.emit('ui:rankingTrack',{track});this.boardEl.append(tab);
      }
      const refresh=h('button','ax-ranking-action','再読込');refresh.type='button';refresh.setAttribute('aria-label','ランキング再読込');refresh.onclick=()=>bus.emit('ui:rankingRefresh',{});
      this.boardEl.append(refresh);
    }
    if (phase === 'dead' || phase === 'menu') this._boardVisible(true, phase);
  }

  _boardVisible(on, phase = S.state?.phase) {
    if (!this.boardEl && on && (this._board || this._score)) this._renderBoard();
    if (!this.boardEl) return;
    if (on) {
      const slot = document.querySelector(phase === 'dead'
        ? '.ax-portal-board-slot-death'
        : '.ax-portal-board-slot-menu');
      if (slot && this.boardEl.parentNode !== slot) slot.appendChild(this.boardEl);
    }
    this.boardEl.classList.toggle('is-on', !!on);
  }

  /* ── hex countdown ─────────────────────────────────────────────────────── */

  _buildHex() {
    this.hexNameEl = h('span', 'ax-portal-hex-name', '符号の呪い');
    this.hexNumEl = h('b', 'ax-portal-hex-num', '0.0');
    this.hexFillEl = h('i', 'ax-portal-hex-fill');
    this.hexEl = h('div', 'ax-portal-hex',
      h('div', 'ax-portal-hex-row',
        h('span', 'ax-portal-hex-mark', '⊖'),
        this.hexNameEl,
        h('span', 'ax-portal-hex-time', this.hexNumEl, h('i', 'ax-portal-hex-unit', '秒'))),
      h('span', 'ax-portal-hex-bar', this.hexFillEl),
      h('div', 'ax-portal-hex-cure', '× か ÷ を共役（N）で解除'));
    this.root.appendChild(this.hexEl);
  }

  _hexArm(p) {
    const now = performance.now();
    const until = Number(p.until);
    this.hexUntil = Number.isFinite(until) && until > now ? until : now + HEX_FALLBACK;
    this.hexSpan = Math.max(1, this.hexUntil - now);

    clearTimeout(this._clearT); this._clearT = 0;
    clearTimeout(this._orphanT); this._orphanT = 0;

    if (!this.hexEl) this._buildHex();
    // An extension that lands mid-release cancels the release.
    this.hexEl.classList.remove('is-clear');
    this.hexNameEl.textContent = '符号の呪い';
    this._placeHex();
    this._markFormula(true);

    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = 0;
    this._tick();
  }

  _hexRelease() {
    this._markFormula(false);
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = 0; }
    clearTimeout(this._orphanT); this._orphanT = 0;
    if (!this.hexEl || this._clearT) return;
    this.hexEl.classList.add('is-clear');
    this.hexNameEl.textContent = '解除!';
    this._clearT = setTimeout(() => this._hexTeardown(), HEX_CLEAR);
  }

  _hexTeardown() {
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = 0; }
    clearTimeout(this._clearT); this._clearT = 0;
    clearTimeout(this._orphanT); this._orphanT = 0;
    this._markFormula(false);
    if (this.hexEl) this.hexEl.remove();
    this.hexEl = this.hexNameEl = this.hexNumEl = this.hexFillEl = null;
  }

  /** The cursed formula bar glows red for as long as the curse holds. */
  _markFormula(on) {
    if (on) {
      if (!this._fb || !this._fb.isConnected) this._fb = document.querySelector('.ax-fb');
      if (this._fb) this._fb.classList.add('is-hexed');
      return;
    }
    this._fb = null;
    // The bar can be rebuilt mid-curse (a language switch re-typesets it), so
    // sweep the document rather than trust the cached node.
    for (const el of document.querySelectorAll('.ax-fb.is-hexed')) el.classList.remove('is-hexed');
  }

  /**
   * The pill hangs under the bar it curses. That bar's height moves with the
   * expression — a Σ or a radical makes it tall — so it is measured, never
   * assumed, and the boss bar's band right below it is stepped over.
   */
  _placeHex() {
    if (!this.hexEl) return;
    const fb = document.querySelector('.ax-fb');
    let top = 214;
    if (fb && fb.offsetHeight) top = Math.round(fb.offsetTop + fb.offsetHeight + 10);
    if (document.querySelector('.ax-boss.is-on')) top += 60;
    this.hexEl.style.top = `${top}px`;
  }

  /* ── prime rain ────────────────────────────────────────────────────────── */

  _rain(p) {
    const density = Math.max(1, Math.min(3, Math.round(Number(p && p.density) || 1)));
    const want = density * RAIN_PER_DENSITY;
    if (this.rainEl.childElementCount + want > RAIN_CAP) this._pruneRain();
    const n = Math.min(want, RAIN_CAP - this.rainEl.childElementCount);
    if (n <= 0) return;

    const now = performance.now();
    const frag = document.createDocumentFragment();
    for (let i = 0; i < n; i++) {
      // One depth per glyph: near ones are big, bright and quick, far ones
      // small, dim and slow. Falls land inside the 1.6–2.4s the design asks for.
      const k = Math.random();
      const dur = 2.4 - k * 0.8;
      const delay = Math.random() * 0.55;
      const g = h('span', 'ax-portal-glyph', PRIMES[(Math.random() * PRIMES.length) | 0]);
      const st = g.style;
      st.setProperty('--x', `${(2 + Math.random() * 96).toFixed(2)}vw`);
      // only read when the player has asked for reduced motion, where the
      // glyph fades in place instead of falling into view
      st.setProperty('--y', `${(8 + Math.random() * 68).toFixed(1)}vh`);
      st.setProperty('--dx', `${((Math.random() * 2 - 1) * (20 + k * 46)).toFixed(0)}px`);
      st.setProperty('--rot', `${(Math.random() * 30 - 15).toFixed(1)}deg`);
      st.setProperty('--sc', (0.8 + k * 0.8).toFixed(2));
      st.setProperty('--o', (0.5 + k * 0.45).toFixed(2));
      st.setProperty('--dur', `${dur.toFixed(2)}s`);
      st.setProperty('--delay', `${delay.toFixed(2)}s`);
      g._axDie = now + (delay + dur) * 1000 + 400;
      g.addEventListener('animationend', () => g.remove(), { once: true });
      frag.appendChild(g);
    }
    this.rainEl.appendChild(frag);
  }

  /** animationend never fires in a display:none subtree; only the cap cares. */
  _pruneRain() {
    const now = performance.now();
    for (const g of [...this.rainEl.children]) if (!(g._axDie > now)) g.remove();
  }

  /* ── toasts ────────────────────────────────────────────────────────────── */

  _toast(p) {
    const text = String((p && p.text) || '').trim().slice(0, TOAST_MAX_CHARS);
    if (!text) return;
    // spelled out, not looked up: `tone: 'constructor'` would hit Object.prototype
    const tone = p && (p.tone === 'help' || p.tone === 'harm') ? p.tone : 'info';

    const el = h('div', 'ax-portal-toast',
      h('span', 'ax-portal-toast-mark', TONE_MARK[tone]),
      h('span', 'ax-portal-toast-txt', text));
    el.dataset.tone = tone;
    this.toastEl.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-in'));

    const rec = { el, hold: 0, kill: 0 };
    rec.hold = setTimeout(() => this._toastOut(rec), TOAST_HOLD);
    this.toasts.push(rec);
    // An evicted toast leaves on its own animation; it just stops being counted.
    while (this.toasts.length > TOAST_MAX) this._toastOut(this.toasts[0]);
  }

  _toastOut(rec) {
    clearTimeout(rec.hold);
    const i = this.toasts.indexOf(rec);
    if (i >= 0) this.toasts.splice(i, 1);
    rec.el.classList.add('is-out');
    clearTimeout(rec.kill);
    rec.kill = setTimeout(() => rec.el.remove(), TOAST_FADE);
  }

  /* ── chat bubbles ──────────────────────────────────────────────────────── */

  /**
   * Same rail as the toasts, so six kills and a busy chat never argue over the
   * same pixels — flex stacking arbitrates. The look is deliberately not the
   * toast's: a toast is the game talking, a bubble is a person.
   * Text lands as textContent (h() only ever mints text nodes) — never markup.
   */
  _chat(p) {
    const name = String((p && p.label) || '').trim().slice(0, CHAT_NAME_CHARS);
    const text = String((p && p.text) || '').trim().slice(0, CHAT_TEXT_CHARS);
    if (!text) return;

    const el = h('div', 'ax-portal-chat',
      h('span', 'ax-portal-chat-name', name || '視聴者'),
      h('span', 'ax-portal-chat-txt', text));
    this.toastEl.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-in'));

    const rec = { el, hold: 0, kill: 0 };
    rec.hold = setTimeout(() => this._chatOut(rec), CHAT_HOLD);
    this.chats.push(rec);
    // Ten comments in one frame leave three counted and the rest fading on a
    // timer — the rail's DOM is bounded on both sides of the cap.
    while (this.chats.length > CHAT_MAX) this._chatOut(this.chats[0]);
  }

  _chatOut(rec) {
    clearTimeout(rec.hold);
    const i = this.chats.indexOf(rec);
    if (i >= 0) this.chats.splice(i, 1);
    rec.el.classList.add('is-out');
    clearTimeout(rec.kill);
    rec.kill = setTimeout(() => rec.el.remove(), CHAT_FADE);
  }

  /* ── like gauge ────────────────────────────────────────────────────────── */

  _buildLike() {
    this.likeNumEl = h('b', 'ax-portal-like-num', String(LIKE_FALLBACK_GOAL));
    this.likeFillEl = h('i', 'ax-portal-like-fill');
    this.likeEl = h('div', 'ax-portal-like',
      h('div', 'ax-portal-like-row',
        h('span', 'ax-portal-like-mark', '♥'),
        h('span', 'ax-portal-like-label',
          'あと', this.likeNumEl, ' いいねで素数の雨')),
      h('span', 'ax-portal-like-bar', this.likeFillEl));
    this.root.appendChild(this.likeEl);
  }

  /**
   * Every batch moves the bar — 連打 has to read as progress, not as a lottery
   * that only pays at the threshold. A fill that runs *backwards* means the
   * wiring already rolled the counter over: that is the payoff, so flash gold
   * and let the bar restart from the remainder.
   */
  _like(p) {
    const goal = Math.max(1, Math.round(Number(p && p.threshold)) || LIKE_FALLBACK_GOAL);
    let left = Math.round(Number(p && p.remaining));
    if (!Number.isFinite(left)) left = goal;
    left = Math.max(0, Math.min(goal, left));

    if (!this.likeEl) this._buildLike();
    this.likeEl.classList.add('is-on');
    if (this._menuGoalEl) this._menuGoalEl.textContent = String(goal);

    // remaining === 0 is the threshold itself: celebrate, then start over empty.
    const k = left === 0 ? 0 : (goal - left) / goal;
    const paid = left === 0 || k < this._likeK - 1e-6;
    this.likeNumEl.textContent = String(left === 0 ? goal : left);

    if (paid) {
      this.likeEl.classList.remove('is-burst');
      // the fill snaps down without animating backwards — a rewinding bar
      // reads as likes being taken away
      this.likeFillEl.style.transition = 'none';
      this.likeFillEl.style.transform = `scaleX(${k.toFixed(4)})`;
      requestAnimationFrame(() => {
        this.likeFillEl.style.transition = '';
        if (this.likeEl) this.likeEl.classList.add('is-burst');
      });
      clearTimeout(this._likeBurstT);
      this._likeBurstT = setTimeout(() => {
        if (this.likeEl) this.likeEl.classList.remove('is-burst');
      }, LIKE_BURST);
    } else {
      this.likeFillEl.style.transform = `scaleX(${k.toFixed(4)})`;
    }
    this._likeK = k;
  }

  /* ── free-action price list ────────────────────────────────────────────── */

  /**
   * The menu of things that cost nothing, ahead of anything that costs money.
   * Built once, on the first event only a live session can produce — a solo
   * run never sees it.
   */
  _liveSeen() {
    if (this._live) return;
    this._live = true;
    if (!this.menuEl) this._buildMenu();
    requestAnimationFrame(() => { if (this.menuEl) this.menuEl.classList.add('is-on'); });
  }

  _buildMenu() {
    this._menuGoalEl = h('b', 'ax-portal-menu-n', String(LIKE_FALLBACK_GOAL));
    const row = (...act) => h('div', 'ax-portal-menu-row',
      h('span', 'ax-portal-menu-act', ...act),
      h('span', 'ax-portal-menu-arrow', '→'),
      h('span', 'ax-portal-menu-reward', '素数の雨'));
    this.menuEl = h('div', 'ax-portal-menu',
      h('div', 'ax-portal-menu-title', '無料アクション'),
      row('いいね', this._menuGoalEl),
      row('シェア'),
      row('コメント !prime'));
    this.root.appendChild(this.menuEl);
  }

  /* ── nameplates ────────────────────────────────────────────────────────── */

  _tag(p) {
    const enemy = p && p.enemy;
    const label = String((p && p.label) || '').trim().slice(0, TAG_NAME_CHARS);
    if (!enemy || !enemy.position || !label) return;

    const had = this.tags.get(enemy);
    if (had) {
      // a re-tag renames the plate and, if it was mid-fade, revives it
      clearTimeout(had.killT);
      had.dying = false;
      had.el.classList.remove('is-out');
      had.el.textContent = label;
      this._tagRun();
      return;
    }

    // The 9th name evicts the oldest — instantly, no fade: an eviction is
    // bookkeeping, and only instant removal keeps a burst of tags bounded.
    while (this.tags.size >= TAG_MAX) this._tagDrop(this.tags.keys().next().value);

    const el = h('div', 'ax-portal-tag', label);
    el.classList.add('is-off');            // invisible until the loop places it
    this.root.appendChild(el);
    this.tags.set(enemy, { enemy, el, dying: false, killT: 0 });
    this._tagRun();
  }

  _untag(enemy) {
    const rec = enemy != null && this.tags.get(enemy);
    if (!rec || rec.dying) return;
    rec.dying = true;
    rec.el.classList.add('is-out');
    rec.killT = setTimeout(() => this._tagDrop(enemy), TAG_FADE);
  }

  _tagDrop(enemy) {
    const rec = this.tags.get(enemy);
    if (!rec) return;
    clearTimeout(rec.killT);
    rec.el.remove();
    this.tags.delete(enemy);
    if (!this.tags.size) this._tagHalt();
  }

  /** One rAF for all plates, alive only while at least one exists. */
  _tagRun() {
    if (!this._tagRaf && this.tags.size) this._tagRaf = requestAnimationFrame(this._tagStep);
  }

  _tagHalt() {
    if (this._tagRaf) { cancelAnimationFrame(this._tagRaf); this._tagRaf = 0; }
  }

  /* ── lifecycle ─────────────────────────────────────────────────────────── */

  clear() {
    this._hexTeardown();
    this.rainEl.textContent = '';
    for (const r of this.toasts) { clearTimeout(r.hold); clearTimeout(r.kill); }
    this.toasts.length = 0;
    for (const r of this.chats) { clearTimeout(r.hold); clearTimeout(r.kill); }
    this.chats.length = 0;
    this.toastEl.textContent = '';
    // Every tagged enemy despawned with the run that just ended.
    this._tagHalt();
    for (const rec of this.tags.values()) { clearTimeout(rec.killT); rec.el.remove(); }
    this.tags.clear();
    // The gauge and the price list stay, like the board: likes accumulate per
    // stream session, and the player's restart does not reset the audience.
  }

  dispose() {
    for (const off of this._offs) off();
    this._offs.length = 0;
    window.removeEventListener('resize', this._onResize);
    this.clear();
    clearTimeout(this._likeBurstT);
    this.root.remove();
    // The board lives on <body>, not in the layer (see _renderBoard), so
    // removing the layer does not take it with it.
    if (this.boardEl) { this.boardEl.remove(); this.boardEl = null; }
    if (_overlay === this) _overlay = null;
  }
}

let _overlay = null;

/** Build the viewer-action layer once. Safe to call from anywhere, any number
 *  of times, before or after the game boots. */
export function initPortalOverlay() {
  if (_overlay && _overlay.root.isConnected) return _overlay;
  const host = document.body || document.documentElement;
  if (!host) return null;
  _overlay = new PortalOverlay(host);
  return _overlay;
}

export default initPortalOverlay;
